import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import {
  getUSPSAMatch,
  getUSPSAMatchRegistrationSummary,
  getUSPSAMatchSetupDetail,
  getMyUSPSAMatchRegistration,
  listCompetitionResults,
  listMatchLeaderboards,
} from "../../lib/leaderboardApi";
import MatchLocationMap from "../Matches/MatchLocationMap";
import DetailOverlay from "./DetailOverlay";
import { MatchSetupRenderer } from "./JsonRenderers";
import { CompetitionErrorState } from "../Competition/CompetitionRoutePanels";
import { formatCalendarDate } from "../../utils/date";
import "./Leaderboard.css";

function formatDate(value) {
  if (!value) return "-";
  return formatCalendarDate(value);
}

function formatStatus(value) {
  return String(value || "unknown")
    .replace(/_/g, " ")
    .replace(/^./, (char) => char.toUpperCase());
}

function isResultsPhaseStatus(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return ["in_progress", "completed", "final", "official", "closed"].includes(normalized);
}

function buildAddressLabel(match) {
  const parts = [
    String(match?.rangeName || "").trim(),
    String(match?.rangeAddress || "").trim(),
    [String(match?.city || "").trim(), String(match?.state || "").trim()].filter(Boolean).join(", ")
  ].filter(Boolean);
  return parts.join(" • ") || "Address not set";
}

function hasMapLocation(match) {
  return Number.isFinite(Number(match?.latitude)) && Number.isFinite(Number(match?.longitude));
}

function normalizeRows(rows = []) {
  return [...rows].sort((left, right) => {
    const lScore = Number(left?.totalScore ?? left?.classificationScore ?? 0);
    const rScore = Number(right?.totalScore ?? right?.classificationScore ?? 0);
    if (lScore !== rScore) {
      return rScore - lScore;
    }
    return String(left?.competitorName || left?.displayName || "").localeCompare(String(right?.competitorName || right?.displayName || ""));
  });
}

function normalizeCanonicalUSPSAResultRows(rows = []) {
  return normalizeRows(
    (Array.isArray(rows) ? rows : [])
      .filter((row) => String(row?.discipline || "").toLowerCase() === "uspsa")
      .map((row) => ({
        ...row,
        sk: row.resultId,
        competitorId: row.participantId,
        competitorName: row.displayName || "Competitor",
        divisionId: row.metadata?.division || row.score?.division || row.divisionId || "",
        totalScore: Number(row.classificationScore ?? row.score?.stagePoints ?? 0),
        scoreDisplay: row.nativeScore || String(row.classificationScore ?? "-"),
        reviewStatus: "confirmed",
      })),
  );
}

function buildUSPSARegistrationFacts(summary) {
  const pending = Number(summary?.pending || 0);
  const confirmed = Number(summary?.confirmed || 0);
  return {
    registered: pending + confirmed,
    confirmed,
    pending,
    registrationOpen: summary?.registrationOpen === true,
  };
}

function USPSALeaderboardMatchDetail() {
  const { matchId } = useParams();
  const [match, setMatch] = useState(null);
  const [setupDetail, setSetupDetail] = useState(null);
  const [registrationSummary, setRegistrationSummary] = useState(null);
  const [myRegistration, setMyRegistration] = useState(null);
  const [leaderboardRows, setLeaderboardRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detailPanel, setDetailPanel] = useState(null);

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const [
          matchResult,
          setupResult,
          registrationResult,
          leaderboardResult,
          canonicalResult,
        ] = await Promise.allSettled([
          getUSPSAMatch(matchId),
          getUSPSAMatchSetupDetail(matchId),
          getUSPSAMatchRegistrationSummary(matchId),
          listMatchLeaderboards({ matchId }),
          listCompetitionResults(matchId),
        ]);

        if (!active) return;
        if (matchResult.status !== "fulfilled") {
          throw matchResult.reason;
        }

        const matchValue = matchResult.value;
        const legacyRows = leaderboardResult.status === "fulfilled" && Array.isArray(leaderboardResult.value)
          ? leaderboardResult.value
          : [];
        const canonicalRows = canonicalResult.status === "fulfilled"
          ? normalizeCanonicalUSPSAResultRows(canonicalResult.value)
          : [];

        setMatch(matchValue);
        setSetupDetail(
          setupResult.status === "fulfilled"
            ? setupResult.value?.setupDetail || { stages: matchValue?.stages || [] }
            : { stages: matchValue?.stages || [] },
        );
        setRegistrationSummary(registrationResult.status === "fulfilled" ? registrationResult.value || null : null);
        setLeaderboardRows(legacyRows.length > 0 ? legacyRows : canonicalRows);
      } catch (loadError) {
        if (!active) return;
        setError(loadError?.message || "Failed to load USPSA competition match detail");
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [matchId]);

  useEffect(() => {
    let active = true;

    async function loadMyRegistrationState() {
      try {
        const registration = await getMyUSPSAMatchRegistration(matchId);
        if (active) {
          setMyRegistration(registration || null);
        }
      } catch {
        if (active) {
          setMyRegistration(null);
        }
      }
    }

    loadMyRegistrationState();
    return () => {
      active = false;
    };
  }, [matchId]);

  const resultsPhase = useMemo(() => isResultsPhaseStatus(match?.status), [match?.status]);
  const rows = useMemo(() => normalizeRows(leaderboardRows), [leaderboardRows]);
  const stageCount = Array.isArray(setupDetail?.stages) ? setupDetail.stages.length : 0;
  const divisionCount = useMemo(() => new Set(rows.map((row) => String(row?.divisionId || "").trim()).filter(Boolean)).size, [rows]);
  const registrationFacts = useMemo(() => buildUSPSARegistrationFacts(registrationSummary), [registrationSummary]);
  const ruleFamilyLabel = useMemo(() => {
    return String(match?.ruleFamily || "competition")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (value) => value.toUpperCase());
  }, [match?.ruleFamily]);

  return (
    <section className="leaderboard-page">
      <Helmet>
        <title>{match?.matchName || "USPSA Match"} | Range Pocket Competition</title>
      </Helmet>

      {loading && (
        <div className="leaderboard-card list-empty-panel status-loading-panel">
          <div className="owner-registration-section-label">Loading</div>
          <strong>USPSA Match</strong>
        </div>
      )}
      {!loading && error && (
        <CompetitionErrorState
          eyebrow="USPSA match"
          title="Match could not load"
          message={error}
          onRetry={() => window.location.reload()}
          links={[
            { to: "/matches", label: "Matches" },
            { to: "/tournaments", label: "Tournaments" },
          ]}
          className="match-detail-error-panel"
        />
      )}

      {!loading && !error && match && (
        <>
          <div className="leaderboard-detail-hero">
            <div className="leaderboard-detail-copy">
              <Link to="/matches" className="leaderboard-detail-back-link">
                <span aria-hidden="true">←</span>
                <span>Matches</span>
              </Link>
              <h1>{match.matchName || "USPSA Match"}</h1>

              <div className="leaderboard-detail-chip-row">
                <span className="status-pill status-completed">{formatStatus(match.publicationState)}</span>
                <span className="status-pill status-in_progress">{formatStatus(match.status)}</span>
                <span className="open-match-chip">{ruleFamilyLabel}</span>
                <span className="open-match-chip">{match.level || "Level I"}</span>
                <span className="open-match-chip">{match.templateKind || "Club Match"}</span>
              </div>

              <div className="leaderboard-detail-facts">
                <div>
                  <span>Start</span>
                  <strong>{formatDate(match.startDate || match.matchDate)}</strong>
                </div>
                <div>
                  <span>End</span>
                  <strong>{formatDate(match.endDate || match.matchDate)}</strong>
                </div>
                <div>
                  <span>Stages</span>
                  <strong>{stageCount}</strong>
                </div>
                <div>
                  <span>Results</span>
                  <strong>{rows.length}</strong>
                </div>
                <div>
                  <span>Divisions</span>
                  <strong>{divisionCount}</strong>
                </div>
              </div>

              <div className="leaderboard-detail-actions">
                <button type="button" className="match-search-button" onClick={() => setDetailPanel("match")}>
                  Match Info
                </button>
                <button type="button" className="match-search-button" onClick={() => setDetailPanel("stage")}>
                  Stage Info
                </button>
              </div>
            </div>

            <div className="leaderboard-detail-map-card">
              {hasMapLocation(match) ? (
                <MatchLocationMap
                  matches={[match]}
                  className="leaderboard-detail-map-shell"
                  activeMatchId={match.matchId}
                  emptyMessage="Map not available."
                />
              ) : (
                <div className="leaderboard-card leaderboard-detail-map-empty">Map not available.</div>
              )}
              <div className="leaderboard-detail-map-address">{buildAddressLabel(match)}</div>
            </div>
          </div>

          {!resultsPhase && (
            <section className="leaderboard-card leaderboard-inline-action-panel public-match-registration-panel">
              <div className="leaderboard-card-row leaderboard-card-row--wrap">
                <h2>Registration</h2>
                <Link
                  to={`/matches/uspsa/${encodeURIComponent(matchId)}/register`}
                  className="match-search-button leaderboard-inline-link"
                >
                  {myRegistration ? "Manage" : "Register"}
                </Link>
              </div>

              <div className="registration-readiness-strip public-match-readiness-strip">
                <div className={`registration-readiness-item ${registrationFacts.registered > 0 ? "is-ready" : "is-neutral"}`}><span>Registered</span><strong>{registrationFacts.registered}</strong></div>
                <div className={`registration-readiness-item ${registrationFacts.confirmed > 0 ? "is-ready" : "is-neutral"}`}><span>Confirmed</span><strong>{registrationFacts.confirmed}</strong></div>
                <div className={`registration-readiness-item ${registrationFacts.pending > 0 ? "is-missing" : "is-ready"}`}><span>Pending</span><strong>{registrationFacts.pending}</strong></div>
                <div className={`registration-readiness-item ${registrationFacts.registrationOpen ? "is-ready" : "is-missing"}`}><span>Status</span><strong>{registrationFacts.registrationOpen ? "Open" : "Closed"}</strong></div>
              </div>
            </section>
          )}

          <section className="leaderboard-card">
            <div className="leaderboard-card-row">
              <h2>Results</h2>
            </div>

            {!resultsPhase && (
              <div className="leaderboard-card match-detail-empty-panel list-empty-panel">
                <div className="owner-registration-section-label">Results</div>
                <strong>Results are not live yet</strong>
                <div className="empty-filter-summary" aria-label="Results state">
                  <span>{String(match?.status || "scheduled").replace(/_/g, " ")}</span>
                </div>
              </div>
            )}
            {resultsPhase && rows.length === 0 && (
              <div className="leaderboard-card match-detail-empty-panel list-empty-panel">
                <div className="owner-registration-section-label">No results</div>
                <strong>No leaderboard rows yet</strong>
                <div className="empty-filter-summary" aria-label="Leaderboard state">
                  <span>Rows: 0</span>
                  <span>{stageCount} stages</span>
                </div>
              </div>
            )}

            {resultsPhase && rows.length > 0 && (
              <div className="leaderboard-grid">
                {rows.map((row, index) => (
                  <div key={`${row.matchId || match.matchId}-${row.sk || row.competitorId || index}`} className="leaderboard-card match-card">
                    <div className="leaderboard-card-row">
                      <strong>{row.competitorName || "Competitor"}</strong>
                      <span className="status-pill status-in_progress">#{index + 1}</span>
                    </div>
                    <div className="leaderboard-meta">{row.divisionId || "Division not set"}</div>
                    <div className="leaderboard-meta">Score: {row.scoreDisplay || Number(row.totalScore || 0).toFixed(2)}</div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <DetailOverlay
            isOpen={detailPanel === "match"}
            title="Match Info"
            onClose={() => setDetailPanel(null)}
          >
            <MatchSetupRenderer setupDetail={setupDetail} />
          </DetailOverlay>

          <DetailOverlay
            isOpen={detailPanel === "stage"}
            title="Stage Info"
            onClose={() => setDetailPanel(null)}
          >
            <MatchSetupRenderer setupDetail={{ stages: setupDetail?.stages || [] }} />
          </DetailOverlay>
        </>
      )}
    </section>
  );
}

export const USPSALeaderboardMatchDetail_Page = {
  path: "/matches/uspsa/:matchId",
  element: <USPSALeaderboardMatchDetail />
};
